import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { isEmpty, isNil } from 'lodash';
import { ValuationAllocationService } from 'api';
import {
  UPDATE_VALUATION_DEFAULT_ERROR_MESSAGE,
  UPDATE_VALUATION_DEFAULT_SUCCESS_MESSAGE,
  VALUATION_BY_ALLOCATION_QUERY_KEY,
} from 'common/constants/services/valuations';
import { throwError } from 'utilities';
import { UpdateValuationParams, UpdateValuationResponse, UseUpdateValuationReturn } from './useUpdateValuation.types';

const updateValuation = async (params: UpdateValuationParams) => {
  const { valuation } = params;
  const { allocation_id: allocationId } = valuation;

  const currentAllocationId = allocationId?.toString();

  if (isEmpty(valuation) || isNil(valuation?.id) || isNil(currentAllocationId)) {
    return throwError(UPDATE_VALUATION_DEFAULT_ERROR_MESSAGE);
  }

  try {
    const updatedValuation = await ValuationAllocationService.apiValuationAllocationUpdate(
      currentAllocationId,
      valuation
    );

    if (updatedValuation?.id) {
      return {
        valuation: updatedValuation,
        responseMessage: UPDATE_VALUATION_DEFAULT_SUCCESS_MESSAGE,
      } as UpdateValuationResponse;
    }

    return throwError(UPDATE_VALUATION_DEFAULT_ERROR_MESSAGE);
  } catch (error) {
    return throwError(UPDATE_VALUATION_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useUpdateValuation = (): UseUpdateValuationReturn => {
  const queryClient = useQueryClient();

  const updateValuationMutation: UseMutationResult<
    UpdateValuationResponse | null, // Response type (null if error)
    Error, // Error type
    UpdateValuationParams // Parameters type
  > = useMutation({
    mutationFn: updateValuation,
  });

  const { isError, isPending: isLoading, isSuccess, mutateAsync } = updateValuationMutation;

  if (isSuccess) {
    updateValuationMutation.reset();

    // Invalidate the queries to refetch the updated data of the Valuation
    queryClient.invalidateQueries({
      queryKey: [
        VALUATION_BY_ALLOCATION_QUERY_KEY, // Valuation by allocation
      ],
    });
  }

  return {
    isError,
    isLoading,
    mutateAsync,
  };
};

export default useUpdateValuation;
